import { Ingredient, isIngredientMacro, isIngredientReal } from '@cf-shared/data-model/ingredient';
import { MealTunerSolved } from '@cf-shared/meal-tuner-interface';

/**
 * Why Auto-Tune had nothing it could do, which decides what the user is told (#5297).
 *
 * The two need different things FROM the user, so they are told apart rather than sharing one
 * message with a sentence bolted on — which used to point at locking even when nothing was
 * locked.
 *
 * Shared by the meal editor's Auto-Tune and the portion screen's Split & Auto-Tune, so both
 * screens decide which dead end it was by the same rule (df-c0a73cdcaa46).
 */
export enum CannotTuneReason {
  /** Some rows are locked or were typed in as macros, and the tuner may change neither. */
  UntunableRows = 'untunable-rows',
  /** Every row was tunable and the tuner still found no answer. The meal needs different food. */
  NoSolution = 'no-solution',
}

/**
 * Say which dead end the tuner hit, given the rows it was asked to tune.
 *
 * Call this only after the tuner answered "no combination fits" — it does not look at the
 * answer, only at what went in. A locked food or a typed-in macro row is something the user can
 * go and change, so its presence decides the answer.
 */
export function classifyCannotTune(ingredients: Ingredient[]): CannotTuneReason {
  const anyIngredientsLocked = ingredients.some(ingredient => isIngredientReal(ingredient) && ingredient.locked);
  const anyQuickMacros = ingredients.some(ingredient => isIngredientMacro(ingredient));

  return (anyIngredientsLocked || anyQuickMacros)
    ? CannotTuneReason.UntunableRows
    : CannotTuneReason.NoSolution;
}

/** How many of the broken meals' ids a report carries, so a cache full of old recipes stays one readable event. */
const INVALID_MEAL_SAMPLE_SIZE = 5;

/**
 * Report the meals the tuner left out because their numbers were broken (a missing weight, a
 * recipe stored with a total weight of 0, a measure whose amount is 0).
 *
 * For a solve over MANY candidate meals, where the tuner answers from the good ones and reports
 * the broken ones beside its answer. One report per solve, never one per meal. Does nothing when
 * no meal was left out.
 *
 * Asked only about a solve that RAN, which is why it takes the solved shape rather than the whole
 * result: a failed solve is the caller's crash report to make, and a second report of the same
 * failure from here would only split it across two Sentry issues. The type is what keeps that
 * true now — a failed solve has no `invalidMeals` to hand over in the first place.
 */
export function reportInvalidTunerMeals(
  logging: { trackSilentProblem(problemName: string, context: any): void },
  results: Pick<MealTunerSolved, 'invalidMeals'>,
  where: string,
): void {
  const invalidMeals = results.invalidMeals ?? [];
  if (invalidMeals.length === 0) return;
  logging.trackSilentProblem('meal_tuner_invalid_meals', {
    where,
    count: invalidMeals.length,
    sample: invalidMeals.slice(0, INVALID_MEAL_SAMPLE_SIZE),
  });
}
